// ./src/components/features/SavedPlaces/PlaceTypeSelector/PlaceTypeSelector.tsx
//
// Dropdown selector for saved place types with icons and color coding.
// Follows CarStatusSelector patterns: custom renderOption with icon + label,
// left section showing current selection icon, and required prop behavior
// that auto-selects the first option and fires onChange.

import React, { useMemo, useCallback, useEffect, useRef } from "react";
import { Select, SelectProps, Group, Text, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";
import { PlaceTypeValue } from "@/utils/api";
import {
  PLACE_TYPE_OPTIONS,
  getPlaceTypeIcon,
  getPlaceTypeColor,
} from "@/config/placeTypeOptions";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

// ============================================
// Types
// ============================================

export interface PlaceTypeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange" | "name"> {
  /** Field name for form binding */
  name?: string;

  /** Currently selected place type value */
  value?: PlaceTypeValue | string | null | undefined;

  /** Callback fired when place type changes: (name, value) */
  onChange?: (name: string, value: PlaceTypeValue | null) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Whether the field is required. When true, auto-selects the first option if value is empty. */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;
}

// ============================================
// Option Data Interface (for renderOption)
// ============================================

interface PlaceTypeOptionData {
  value: string;
  label: string;
  icon: string;
  color: string;
}

// ============================================
// Component
// ============================================

const PlaceTypeSelectorComponent: React.FC<PlaceTypeSelectorProps> = ({
  name = "placeType",
  value,
  onChange,
  placeholder,
  required = false,
  error,
  disabled = false,
  size = "md",
  label,
  ...selectProps
}) => {
  const { t } = useTranslation(["savedPlaces", "common"]);

  const defaultPlaceholder =
    placeholder ||
    t("savedPlaces:form.fields.placeType.placeholder", {
      defaultValue: "Select type",
    });
  const defaultLabel =
    label ??
    t("savedPlaces:form.fields.placeType.label", {
      defaultValue: "Place Type",
    });

  // Normalize value to string
  const normalizedValue = useMemo(() => {
    if (value == null || value === "") return null;
    return String(value);
  }, [value]);

  // Build options with translated labels
  const placeTypeOptions = useMemo((): PlaceTypeOptionData[] => {
    return PLACE_TYPE_OPTIONS.map((option) => ({
      value: option.value,
      label: t(`savedPlaces:${option.labelKey}`, { defaultValue: option.value }),
      icon: option.icon,
      color: option.color,
    }));
  }, [t]);

  // Current selection icon + color for left section
  const currentIcon = useMemo(
    () => getPlaceTypeIcon(normalizedValue),
    [normalizedValue],
  );
  const currentColor = useMemo(
    () => getPlaceTypeColor(normalizedValue),
    [normalizedValue],
  );

  // Handle change
  const handleChange = useCallback(
    (selectedValue: string | null) => {
      onChange?.(name, selectedValue as PlaceTypeValue | null);
    },
    [name, onChange],
  );

  // Auto-select first option when required and value is empty
  const hasAutoSelectedRef = useRef(false);

  useEffect(() => {
    if (
      required &&
      !normalizedValue &&
      !hasAutoSelectedRef.current &&
      placeTypeOptions.length > 0
    ) {
      hasAutoSelectedRef.current = true;
      onChange?.(name, placeTypeOptions[0].value as PlaceTypeValue);
    }
  }, [required, normalizedValue, placeTypeOptions, onChange, name]);

  // Reset auto-select guard when value is externally cleared
  useEffect(() => {
    if (normalizedValue) {
      hasAutoSelectedRef.current = false;
    }
  }, [normalizedValue]);

  // Render option for dropdown
  const renderOption: SelectProps["renderOption"] = useCallback(
    ({ option }: any) => {
      const optionData = option as PlaceTypeOptionData;
      return (
        <Group gap="sm" wrap="nowrap">
          <AppIcon
            iconName={optionData.icon}
            size={20}
            color={`var(--mantine-color-${optionData.color}-6)`}
          />
          <Text size="md" fw={500}>
            {optionData.label}
          </Text>
        </Group>
      );
    },
    [],
  );

  // Left section icon
  const leftSection = useMemo(() => {
    if (!normalizedValue) return undefined;
    return (
      <AppIcon
        iconName={currentIcon}
        size={18}
        color={`var(--mantine-color-${currentColor}-6)`}
      />
    );
  }, [normalizedValue, currentIcon, currentColor]);

  return (
    <Box>
      <Select
        {...selectProps}
        label={defaultLabel}
        size={size}
        value={normalizedValue}
        onChange={handleChange}
        placeholder={defaultPlaceholder}
        clearable={!required}
        required={required}
        allowDeselect={!required}
        disabled={disabled}
        error={error}
        data={placeTypeOptions}
        leftSection={leftSection}
        renderOption={renderOption}
        nothingFoundMessage={t("common:form.noOptions", {
          defaultValue: "No options available",
        })}
        comboboxProps={DRAWER_COMBOBOX_PROPS}
      />
    </Box>
  );
};

export const PlaceTypeSelector = React.memo(PlaceTypeSelectorComponent);